import _ from 'lodash'
import HttpFactory from '../factory'
import type { UIObservation } from '../types/UIObservation'
import { INSULINE_CODE } from '../types/directus/observationDefinition'
import { PerformerTypeEnum } from '../types/observation'
import type { AnalyticsSurveyModel, SurveyDashboard } from '../types/survey/surveyAnalytics'
import type { SurveyAnswer } from '../types/survey/surveyAnswer'
import type { SurveyAnswerAttachment } from '../types/survey/surveyAttachment'
import type { SurveyInstance, CreateSurveyInstance } from '../types/survey/surveyInstance'
import type { ISurveyModelSummaryDto, SurveyModel } from '../types/survey/surveyModel'

export type UpdateSurveyInstance = Pick<SurveyInstance, 'generateDocument'>

class SurveysModule extends HttpFactory<any[]> {
  private MODELS_PATH = '/surveys/models'
  private ANALYTICS_PATH = '/surveys/analytics'
  private INSTANCES_PATH = '/surveys/instances'
  private SCHEDULE_INSTANCES_PATH = '/surveys/scheduled-instances'

  async getModels(type: string, purpose: string, instantiable: boolean = false): Promise<ISurveyModelSummaryDto[]> {
    let arr: ISurveyModelSummaryDto[] = []
    let nextPageUrl = `?pageSize=100`
    let params: any = {}
    if (type) params.type = type
    if (purpose) params.purpose = purpose
    if (instantiable) params.instantiable = true
    do {
      await this.call<any>('GET', `${this.MODELS_PATH}/${nextPageUrl}`, undefined, { params }).then((response) => {
        arr = arr.concat(response.items)
        nextPageUrl = response.nextPageUrl
      })
    } while (nextPageUrl)
    return arr
  }

  async getModel(modelID: string, version = null): Promise<SurveyModel> {
    const url = version ? `${this.MODELS_PATH}/${modelID}/${version}` : `${this.MODELS_PATH}/${modelID}`
    return await this.call<any>('GET', url)
  }

  async getModelByType(type: string): Promise<ISurveyModelSummaryDto> {
    return await this.call<any>('GET', `${this.MODELS_PATH}?type=${type}`)
  }

  async getModelQuestion(modelID: string, version: string): Promise<SurveyModel> {
    return await this.call<any>('GET', `${this.MODELS_PATH}/${modelID}/${version}`)
  }

  async getInstance(id: string): Promise<SurveyInstance> {
    return await this.call<any>('GET', `${this.INSTANCES_PATH}/${id}`)
  }

  async getScheduledInstance(patientId: string): Promise<any> {
    return await this.call<any>('GET', `${this.SCHEDULE_INSTANCES_PATH}?targetId=${patientId}`)
  }

  async postScheduledInstance(data: object): Promise<any> {
    return await this.call<any>('POST', `${this.SCHEDULE_INSTANCES_PATH}`, data)
  }

  async patchScheduledInstance(id: string, revision: string, data: object): Promise<any> {
    return await this.call<any>('PATCH', `${this.SCHEDULE_INSTANCES_PATH}/${id}`, data, {
      params: {
        revision,
      },
    })
  }

  async deleteScheduledInstance(patientId: string): Promise<any> {
    return await this.call<any>('DELETE', `${this.SCHEDULE_INSTANCES_PATH}/${patientId}`)
  }

  async postInstance(payload: CreateSurveyInstance, initiateWithPreviousAnswer: Boolean): Promise<SurveyAnswer> {
    return await this.call<any>(
      'POST',
      `${this.INSTANCES_PATH}/?initiateWithPreviousAnswer=${initiateWithPreviousAnswer}`,
      payload,
    )
  }

  async getReportInstance(id: string, patientId: string): Promise<SurveyInstance[]> {
    return await this.call<any>(
      'GET',
      `${this.INSTANCES_PATH}?targetId=${patientId}&relatedEntityType=eocReport&relatedEntityId=${id}`,
    )
  }

  async getReportInstanceByPatientId(patientId: string): Promise<SurveyInstance[]> {
    return await this.call<any>('GET', `${this.INSTANCES_PATH}?targetId=${patientId}`)
  }

  async patchInstance(id: string, revision: string, payload: UpdateSurveyInstance): Promise<SurveyAnswer> {
    return await this.call<any>('PATCH', `${this.INSTANCES_PATH}/${id}`, payload, {
      params: {
        revision,
      },
    })
  }

  async getEncounterInstance(id: string, patientId: string): Promise<any> {
    return await this.call<any>(
      'GET',
      `${this.INSTANCES_PATH}?targetId=${patientId}&relatedEntityType=encounter&relatedEntityId=${id}`,
    )
  }

  async getAnswers(id: string): Promise<SurveyAnswer> {
    return await this.call<any>('GET', `${this.INSTANCES_PATH}/${id}/answers`)
  }

  async postAnswers(id: string, answers: any, completed = false): Promise<SurveyAnswer> {
    return await this.call<any>('POST', `${this.INSTANCES_PATH}/${id}/answers`, {
      status: !completed ? 'draft' : 'completed',
      answers,
    })
  }

  async patchAnswers(id: string, answers: any, completed = false): Promise<SurveyAnswer> {
    return await this.call<any>('PATCH', `${this.INSTANCES_PATH}/${id}/answers`, {
      status: !completed ? 'draft' : 'completed',
      answers,
    })
  }

  async uploadAttachment(id: string, formData: any): Promise<SurveyAnswerAttachment> {
    return await this.call<any>('POST', `${this.INSTANCES_PATH}/${id}/answers/attachments`, formData)
  }

  async getDownloadLink(instanceId: string, attachmentId: string): Promise<any> {
    return await this.call<any>(
      'GET',
      `${this.INSTANCES_PATH}/${instanceId}/answers/attachments/${attachmentId}/download-link`,
    )
  }

  async deleteAttachment(id: string, idDelete = null): Promise<void> {
    if (idDelete) {
      return await this.call<any>('DELETE', `${this.INSTANCES_PATH}/${id}/answers/attachments/${idDelete}`)
    } else {
      // This will not work the endpoint does not exist
      return await this.call<any>('DELETE', `${this.INSTANCES_PATH}/${id}/answers/attachments`)
    }
  }

  async postModel(): Promise<SurveyModel> {
    return await this.call<any>('POST', `${this.MODELS_PATH}`, {
      title: 'Nouveau questionnaire',
      questions: {},
    })
  }

  async patchModelStructure(model: any, questions: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        title: questions.title,
        questions,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async publishModel(model: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        published: true,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async activeModel(model: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        active: true,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async unactiveModel(model: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        active: false,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async patchModelType(model: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        type: model.type,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async patchModelPurpose(model: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        purpose: model.purpose,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async patchModelDocumentTemplateId(model: any): Promise<SurveyModel> {
    return await this.call<any>(
      'PATCH',
      `${this.MODELS_PATH}/${model.modelId}`,
      {
        documentTemplateId: model.documentTemplateId,
      },
      {
        params: {
          revision: model.revision,
        },
      },
    )
  }

  async getAnalyticsModel(): Promise<AnalyticsSurveyModel> {
    return await this.call<any>('GET', `${this.ANALYTICS_PATH}/models`)
  }

  async getAnalyticsDashboard(modelID: string, version: string, query: string): Promise<SurveyDashboard> {
    return await this.call<any>('GET', `${this.ANALYTICS_PATH}/model/${modelID}/${version}/dashboards`, undefined, {
      params: query,
    })
  }

  async getAnalyticsDashboardDiabete(patientId: string): Promise<UIObservation[]> {
    let data: UIObservation[] = []
    await this.call<any>(
      'GET',
      `${this.ANALYTICS_PATH}/dashboards?modelType=diabeteFollowUp&withMatrix=medicationAdministration_insulin&patientId=${patientId}&withUnique=true`,
    ).then((response) => {
      response.answers.forEach((answer: any, indexAnswer: number) => {
        answer['insulin-injection'].forEach((injection: any, indexInnjection: number) => {
          if (injection.date && new Date(injection.date).getTime() < new Date().getTime()) {
            data.push({
              id: INSULINE_CODE + indexAnswer + indexInnjection,
              code: INSULINE_CODE,
              value: { valueFloat: injection.value },
              injectionMode: injection.injectionMode.toLowerCase() == 'basalpump' ? 'continue' : 'discreet',
              injectionName: injection.injectionMode,
              commercialName: injection.commercialName,
              effectiveAt: injection.date,
              endAt: injection.untilDate,
              endAtDate: new Date(injection.untilDate),
              performer: {
                type: PerformerTypeEnum.patient,
              },
            } as UIObservation)
          }
        })
      })
    })
    return _.sortBy(data, ['injectionMode', 'effectiveAt'])
  }
}

export default SurveysModule
